<!-- TEMPLATE -->
<template>
  <div class="wrapper">
    <dean-dialog
      title="Transfer Order Lines to Other Customer (Contract Takeover)"
      :large="true"
      :addClose="true"
      @close="close"
    >
      <slot name="load">

      </slot>
      <dean-notification :is-modal="true">

      </dean-notification>

      <div class="subtitle-box">
        <span class="subtitle">INFO: Bij het overdragen van de orderregel worden alle gerelateerde add-ons overgedragen.</span>
      </div>

      <dean-form-row id="customerCode" label="Customer Code" v-model="customerCode" type="text" @input="handleCustomerCodeInput"
                     key="customerCode"
                     :requiredField="true" :warning="$v.customerCode.$error"></dean-form-row>

      <dean-select-form-row 
        id="agreement" 
        label="Agreement" 
        placeholder="Select agreement"
        :clearable="true"
        :value="currentlySelectedOption(selectedAgreementId, agreementOptions)"
        :options="agreementOptions"
        :requiredField="true"
        :warning="$v.selectedAgreementId.$error"
        @input="valueChanged($event, 'agreementId')"
      ></dean-select-form-row>

      <div class="dean-table">
        <div>
          <table>
            <thead>
              <tr>
                <th style="width: 100px;">
                  <dean-input-switch
                      :id="'orderLine-all'"
                      :checked="selectedOrderLines.length === orderLines.length && orderLines.length > 0"
                      @checked="inputSelectAllSwitchChanged"
                      label="Ja"
                  ></dean-input-switch>
                </th>
                <th>Productnaam</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(orderLine, index) in orderLines" :key="index">
                <td class="center">
                  <dean-input-switch
                      :id="'orderLine-' + orderLine.id"
                      :checked="selectedOrderLines.includes(orderLine.id)"
                      @checked="(value) => inputSwitchChanged(orderLine.id, value)"
                      label="Ja"
                  ></dean-input-switch>
                </td>
                <td>{{ getProductName(orderLine) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <span slot="dean-dialog-button-group">
        <dean-button :danger="true" :dialog-button="true" @click.native="close()">Annuleren</dean-button>
        <dean-button :success="true" :dialog-button="true" @click.native="save()">Versturen</dean-button>
      </span>
    </dean-dialog>
  </div>
</template>

<!-- SCRIPT -->
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import axios from 'axios';
import {
  // SVG
  warningIcon,
  // COMPONENTS
  deanDialog,
  deanDialogButtonGroup,
  deanCheckbox, saveIcon,
} from '@deanone/dean-components';
import deanSelectFormRow from '@/components/deanComponents/deanSelectFormRow.vue';
import deanButton from '@/components/deanComponents/deanButton.vue';
import DeanNotification from '@/components/deanComponents/deanNotification.vue';
import deanFormRow from '@/components/deanComponents/deanFormRow.vue';
import {deanDialogBus} from '@/main';
import {validationMixin} from 'vuelidate';
import {required} from 'vuelidate/lib/validators';
import {Validations} from 'vuelidate-property-decorators';
import DateInput from '@/components/DateInput.vue';
import DeanInputSwitch from "@/components/deanComponents/deanInputSwitch.vue";
import {getPhoneNumberFromOrderLine} from "@/mixins/additionalDataPhoneNumber";
import {createNotification} from "@/mixins/validations";
import {ServiceTypeEnum} from "@/enums/ServiceTypeEnum";
import {OrderStatusIdEnum} from "@/enums/OrderStatusEnum";

@Component({
    components: {
      DeanInputSwitch,
      warningIcon,
      DeanNotification,
      deanDialog,
      deanButton,
      deanFormRow,
      deanSelectFormRow,
      deanDialogButtonGroup,
      deanCheckbox,
      DateInput,
    },
    mixins: [validationMixin],
  })

  export default class OrderMigrateOrderLinesDialog extends Vue {
    // DATA
    @Prop() public payloadData!: {
      [key: string]: {
        [key: string]: string,
      };
    };

    @Validations()
    get validations() {
      return {
        customerCode: {required},
        selectedAgreementId: {required},
      };
    }

    public orderLines: any = [];
    public customerCode: string = '';
    public selectedOrderLines: any = [];
    private selectedAgreementId: string = '';
    private agreementOptions: Array<{ label: string; value: string }> = [];
    private searchTimeout: number | null = null;

    // METHODS
    public close() {
      deanDialogBus.$emit('close');
    }

    public save() {
      this.$v.$touch();

      // @ts-ignore-next-line
      this.$emit('setLoad', true);

      if (!this.$v.$invalid) {
        axios.post('/-/trade/api/order.lines.transfer', {
          customerCode: this.customerCode,
          mainOrderLineIds: this.selectedOrderLines,
          agreementId: this.selectedAgreementId,
        }).then(
            (response) => {
              deanDialogBus.$emit('saved');

              const successMessage = 'Je hebt de gefactureerde orderregels succesvol gemigreerd naar klant ' + this.customerCode + '.';

              this.$toast({
                icon: saveIcon,
                title: 'Opgeslagen',
                text: successMessage,
                theme: 'success',
              });

              this.close();
            },
            (error) => {
              const errorData = error.response.data.errors;

              const errorCode = Array.isArray(errorData) ? errorData[0].code : errorData.code;
              let errorMessage = Array.isArray(errorData) ? errorData[0].message : errorData.message;

              if (errorCode === 'INCORRECT_ADD_ON_ORDER_LINE_STATE') {
                const orderNumber = errorMessage.match(/order number (\w+)/)?.[1] || null;
                errorMessage = 'Voeg toe met ordernummer ' + orderNumber + ' is niet in de gefactureerde status. Zorg ervoor dat het in de gefactureerde status staat voordat je de migratie uitvoert!'
              }

              if (errorCode === 'INCORRECT_CUSTOMER_CODE') {
                errorMessage = 'Onjuiste klantcode. U mag geen orderregels overdragen aan dezelfde klant.'
              }

              if (errorCode === 'CUSTOMER_NOT_FOUND') {
                errorMessage = 'Klant met de opgegeven klantcode bestaat niet.'
              }

              if (errorCode === 'INACTIVE_CUSTOMER') {
                errorMessage = 'U mag geen orderregels overdragen aan een inactieve klant.'
              }

              if (errorCode === 'AGREEMENT_NOT_FOUND') {
                errorMessage = 'Overeenkomst niet gevonden!'
              }

              if (errorCode === 'MAIN_ORDER_LINE_REQUIRED') {
                errorMessage = 'Er moet minimaal één orderregel worden opgegeven!'
              }

              this.$toast({
                icon: warningIcon,
                title: 'Waarschuwing',
                text: errorMessage,
                theme: 'warning',
              });
            },
        ).finally(() => {
          this.$emit('setLoad', false);
        });
      } else {
        this.$emit('setLoad', false);
        this.$toast(createNotification(this.$v));
      }
    }

    public inputSelectAllSwitchChanged(field: { id: string, value: boolean }) {
      if (field.value) {
        this.selectedOrderLines = this.orderLines.map((orderLine: any) => orderLine.id);
      } else {
        this.selectedOrderLines = [];
      }
    }

    public inputSwitchChanged(selectedOrderLineId: string, field: { id: any, value: boolean }) {
      if (field.value) {
        if (!this.selectedOrderLines.includes(selectedOrderLineId)) {
          this.selectedOrderLines.push(selectedOrderLineId);
        }
      } else {
        this.selectedOrderLines = this.selectedOrderLines.filter(
            (orderLineId: string) => orderLineId !== selectedOrderLineId
        );
      }
    }

    private getProductName(orderLine: any): string {
      let productName = orderLine.product.class
          ? `${orderLine.product.class} - ${orderLine.product.name}` : orderLine.product.name;

      if (orderLine?.additionalData === null || orderLine?.additionalData === undefined) {
        return productName;
      }

      let phoneNumber = getPhoneNumberFromOrderLine(orderLine);

      if (phoneNumber) {
        productName += ` (${phoneNumber})`;
      }

      return productName;
    }

    public created() {
      this.$emit('setLoad', true);

      const promises = [];

      promises.push(
          axios.post('/-/trade/api/order.line.list', {
            transactionId: this.$route.params.transactionId,
            meta: {
              startIndex: 0,
              numberOfItems: 1000,
            },
          }).then(
              (response: any) => {
                this.orderLines = response.data.data.filter((orderLine: any) => (
                    (orderLine.service.name === ServiceTypeEnum.MOBILE_ODIDO
                    || orderLine.service.name === ServiceTypeEnum.MOBILE_KPN)
                    && orderLine.status.id === OrderStatusIdEnum.INVOICED
                    && orderLine.isMainProduct === true
                ));
              },
              (error) => {
                this.$toast(this.$store.state.main.notification.messages.couldNotReceiveDataModal);
              },
          ),
      );

      Promise.all(promises).finally(() => {
        this.$emit('setLoad', false);
      });
    }

    public handleCustomerCodeInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = window.setTimeout(() => {
        this.searchCustomer();
      }, 500);
    }

    private async searchCustomer() {
      if (!this.customerCode) {
        this.agreementOptions = [];
        this.selectedAgreementId = '';
        return;
      }

      try {
        const customerResponse = await axios.post('/-/trade/api/customer.info', {
          customerCode: this.customerCode,
        });

        const partnerId = customerResponse.data.partnerId;
        
        const agreementsResponse = await axios.post('/-/trade/api/partner.agreements.list', {
          id: partnerId,
          activeOnly: true,
        });

        this.agreementOptions = agreementsResponse.data.data.map((agreement: any) => {
          const option = {
            label: agreement.description || agreement.typeName,
            value: agreement.id,
          };
          
          return option;
        });

        if (this.agreementOptions.length > 0) {
          this.selectedAgreementId = this.agreementOptions[0].value;
        }
      } catch (error) {
        this.agreementOptions = [];
        this.selectedAgreementId = '';
      }
    }

    public currentlySelectedOption(value: string, options: Array<{ label: string; value: string }>) {
      if (!value) return null;
      return options.find(option => option.value === value) || null;
    }

    public valueChanged(field: any, id: string) {
      this.selectedAgreementId = field.value;
    }

    public beforeDestroy() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
    }
  }
</script>

<!-- STYLE -->
<style lang="scss" scoped>

@import '~@deanone/dean-components/src/scss/vars';

.subtitle-box {
  padding-bottom: 10px;
  padding-left: 20px;

  .subtitle {

    color: #007AFF;
    font-style: italic;
  }
}

:deep(.dialog.large) {
  .dean-dialog-container {
    width: 800px;
    min-width: 768px;
    max-width: 90%;
  }

  &.win-dialog-modal-large {
    .dean-dialog-content {
      .dean-dialog-slot {
        overflow-y: auto;
      }
    }
  }

  &.win-dialog-modal-small {
    .dean-dialog-content {
      .dean-dialog-slot {
        overflow-y: visible;
      }
    }
  }
}

// DEAN TABLE
.dean-table {
  padding: 10px;

  table {
    border-collapse: collapse;
    margin: 0;
    padding: 0;
    min-width: 100%;
    overflow-wrap: break-word;
    @include hyphens(auto);

    a {
      color: black;
    }

    tr {
      padding: 10px;
    }

    .hover {
      cursor: pointer;
      @media #{$medium} {
        &:hover {
          $hover: var(--color-light-shade-4, $color-light-shade-4);
          background: $hover;
        }
      }
    }

    @media #{$medium} {
      .subHeader td {
        font-weight: 700 !important;
        //background: var(--color-light-shade-4, $color-light-shade-4);
        border-bottom: 1.5px solid var(--color-light, $color-light) !important;
        // border-bottom: 1px solid var(--color-secondary, $color-secondary);
      }
    }

    th {
      font-weight: 400;
      font-weight: normal;
      @include table-row-height;
      padding: 10px;
      background: var(--color-light-shade-2, $color-light-shade-2);
      border-right: 4px solid white;
      border-left: 4px solid white;
      @include border-radius(10px 10px 0 0);
      //text-transform: lowercase;
      /*
        &:first-letter {
          text-transform: uppercase;
        }
        */

      &:first-child {
        border-left: 0px;
        border-right: 4px solid white;
      }

      &:last-child {
        border-left: 4px solid white;
        border-right: 0px;
      }

      .active .arrow {
        opacity: 1;
      }
    }

    td {
      padding: 5px 10px 5px 10px;
      border-bottom: 1px solid var(--color-light-shade-3, $color-light-shade-3);
      border-right: 4px solid white;
      border-left: 4px solid white;
      @include table-row-height;

      &:first-child {
        border-left: 0px;
        border-right: 4px solid white;
      }

      &:last-child {
        border-left: 4px solid white;
        border-right: 0px;
      }
    }
  }

  .center {
    @include display-flex();
    align-items: center;
    justify-content: center;
  }

  .left {
    @include display-flex();
    align-items: center;
    justify-content: flex-start;
  }

  .right {
    @include display-flex();
    align-items: center;
    justify-content: flex-end;
  }

  @mixin gridView() {
    table {
      border: 0;
      paddng: 0;

      tbody {
        @include display-flex;
        flex-wrap: wrap;
        width: 100%;
      }

      thead {
        border: none;
        clip: rect(0 0 0 0);
        height: 1px;
        margin: -1px;
        padding: 0;
        position: absolute;
        width: 1px;
      }

      tr {
        display: block;
        padding: 0;
        justify-content: flex-start;
        @include border-radius(10px);
        @include box-shadow(0px 3px 5px rgba(0, 0, 0, 0.075));
        padding-bottom: 10px;
        margin: 5px;
        @include calc("width", "25% - 10px");
        @media #{$large-only} {
          @include calc("width", "33.33333333333% - 10px");
        }
        @media #{$medium-only} {
          @include calc("width", "50% - 10px");
        }
        @media #{$small-only} {
          width: 100%;
          margin: 0px 0px 20px 0px;
        }
      }

      th {
        border-left: 0;
        border-right: 0;
        padding: 15px;
      }

      td {
        border-bottom: 1px solid
        var(--color-light-shade-3, $color-light-shade-3);
        display: flex;
        width: 100%;
        padding: 15px;
        border-left: 0;
        border-right: 0;
        text-align: right;
        max-width: 100%;
        justify-content: space-between;

        &:first-child {
          background: var(--color-light-shade-2, $color-light-shade-2);
          @include border-radius(10px 10px 0px 0px);
          border: 0;

          &:before {
            /** aria-label has no advantage, it won't be read inside a table
              content: attr(aria-label);*/
            content: attr(data-label);
            float: left;
          }
        }

        &:before {
          /** aria-label has no advantage, it won't be read inside a table
            content: attr(aria-label);*/
          content: attr(data-label);
          float: left;
        }

        &:last-child {
          border: 0;
          @include border-radius(0 0 10px 10px);
        }
      }
    }
  }

  .grid-view {
    @include gridView();
  }

  @media #{$small-only} {
    @include gridView();
  }
}

</style>
